/* eslint-disable no-console */
import { useAuth0 } from '@auth0/auth0-react';
import {
  ITokenClaims,
  TokenCustomClaimKeysEnum,
} from '@sitecore-ui/portal-singular';
import { useQuery } from 'react-query';
import { useAuthenticatedAxios } from '../apiUtils/AxiosProvider';
import { getUserRoles } from './getUserRoles';

export { TokenCustomClaimKeysEnum };

export const useAuth0TokenClaims = () => {
  const { getIdTokenClaims, user, getAccessTokenSilently } = useAuth0();
  const axiosContext = useAuthenticatedAxios();
  const organizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];

  const {
    data: claims,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ['auth0-token-claims', organizationId],
    async () => {
      if (!organizationId) {
        return undefined;
      }

      try {
        await getAccessTokenSilently({ organization_id: organizationId });

        const tokenClaims = await getIdTokenClaims();
        if (!tokenClaims) {
          return undefined;
        }

        const roles: string[] = [
          ...(tokenClaims[TokenCustomClaimKeysEnum.ROLES] || []),
        ];

        if (
          tokenClaims.sc_has_more_roles ||
          tokenClaims.sc_has_more_tenant_roles
        ) {
          const userRoles = await getUserRoles(axiosContext, organizationId);

          const newRoles =
            userRoles
              ?.map((role) => `[${role.scope}]\\${role.role}`)
              .filter((role) => !roles.includes(role)) || [];

          const allRoles = [...roles, ...newRoles];

          return {
            ...tokenClaims,
            [TokenCustomClaimKeysEnum.ROLES]: allRoles,
          } as ITokenClaims;
        }

        return tokenClaims as ITokenClaims;
      } catch (error) {
        console.error('Failed to fetch token claims:', error);
        throw error;
      }
    },
    {
      enabled: !!organizationId,
      staleTime: Infinity,
      retry: false,
    },
  );

  return { claims, isLoading, isError, refetch };
};
